export type ScheduleFrequency = 'WEEKLY' | 'BIWEEKLY' | 'FOUR_WEEKLY' | 'MONTHLY' | 'QUARTERLY';
export type ScheduleDayOfMonth =
	| 'FIRST_WORKING_DAY'
	| 'LAST_WORKING_DAY'
	| 'DAY_1'
	| 'DAY_15'
	| 'LAST_DAY'
	| 'FIRST_MONDAY'
	| 'LAST_FRIDAY';
export type ScheduleWeekday = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

export const FREQUENCIES: ScheduleFrequency[] = ['WEEKLY', 'BIWEEKLY', 'FOUR_WEEKLY', 'MONTHLY', 'QUARTERLY'];
export const DAYS_OF_MONTH: ScheduleDayOfMonth[] = [
	'FIRST_WORKING_DAY',
	'LAST_WORKING_DAY',
	'DAY_1',
	'DAY_15',
	'LAST_DAY',
	'FIRST_MONDAY',
	'LAST_FRIDAY',
];
// The day rules a frequency offers. A quarterly schedule runs on the first or last days of a quarter, so
// it has no 15th.
export const daysOfMonthFor = (frequency: ScheduleFrequency): ScheduleDayOfMonth[] =>
	frequency === 'QUARTERLY' ? DAYS_OF_MONTH.filter(day => day !== 'DAY_15') : DAYS_OF_MONTH;

export const WEEKDAYS: ScheduleWeekday[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

// Every half hour of the day, 00:00 to 23:30. Runs only go out on the hour and the half hour, so every
// slot can fire exactly when it says.
const SLOTS_PER_DAY = 48;
export const TIME_SLOTS = Array.from({length: SLOTS_PER_DAY}, (_, index) => {
	const hours = String(Math.floor(index / 2)).padStart(2, '0');
	const minutes = index % 2 === 0 ? '00' : '30';
	return `${hours}:${minutes}`;
});

export const DEFAULT_DAY_OF_MONTH: ScheduleDayOfMonth = 'FIRST_WORKING_DAY';
export const DEFAULT_WEEKDAYS: ScheduleWeekday[] = ['MONDAY'];
export const NEW_SCHEDULE = {
	frequency: 'MONTHLY' as ScheduleFrequency,
	dayOfMonth: DEFAULT_DAY_OF_MONTH as ScheduleDayOfMonth | null,
	weekdays: [] as ScheduleWeekday[],
	timeOfDay: '07:00',
};

export const isWeeklyFrequency = (frequency: ScheduleFrequency): boolean =>
	frequency === 'WEEKLY' || frequency === 'BIWEEKLY' || frequency === 'FOUR_WEEKLY';

// The locales whose people read times on a 12-hour clock: English (US) and English (UK). Every other
// Forecast language reads them on a 24-hour clock.
const TWELVE_HOUR_LOCALES = ['en-US', 'en-GB'];

export const usesTwelveHourClock = (locale: string): boolean => TWELVE_HOUR_LOCALES.includes(locale);

// A stored HH:mm time as the drawer shows it: 15:00 reads 3:00pm on a 12-hour clock and stays 15:00 on a
// 24-hour one.
export const formatTimeOfDay = (timeOfDay: string, twelveHour: boolean): string => {
	if (!twelveHour) {
		return timeOfDay;
	}
	const [hours, minutes] = timeOfDay.split(':').map(Number);
	const suffix = hours < 12 ? 'am' : 'pm';
	const clockHour = hours % 12 === 0 ? 12 : hours % 12;
	return `${clockHour}:${String(minutes).padStart(2, '0')}${suffix}`;
};

const lower = (value: string): string => value.toLowerCase();

export const FREQUENCY_MESSAGE = (frequency: ScheduleFrequency): string => `scheduling.drawer.frequency.${lower(frequency)}`;
export const DAY_OF_MONTH_MESSAGE = (day: ScheduleDayOfMonth): string => `scheduling.drawer.day.${lower(day)}`;
export const WEEKDAY_MESSAGE = (day: ScheduleWeekday): string => `scheduling.drawer.weekday.${lower(day)}`;
export const WEEKDAY_SHORT_MESSAGE = (day: ScheduleWeekday): string => `scheduling.drawer.weekday_short.${lower(day)}`;
